import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Student } from '../../../../utils/firebase';
import Card from '../../../../components/ui/Card';
import { Box2dPhysics } from './marbleRoulette/Box2dPhysics';
import { Camera } from './marbleRoulette/Camera';
import type { StageDef } from './marbleRoulette/data/maps';
import { maruStages as stages } from './marbleRoulette/data/maruStages';
import './MaruMarbleRace.css';
import { initialZoom, zoomThreshold, themeDark } from './marbleRoulette/constants';
import type { MapEntityState } from './marbleRoulette/types/MapEntity.type';

interface PinballLotteryProps {
  students: Student[];
}

interface SimMarble {
  id: number;
  student: Student;
  finished: boolean;
  finishOrder?: number;
  lastX: number;
  lastY: number;
  lastAngle: number;
  stuckMs: number;
}

function marbleSpawnXY(order: number, max: number): { x: number; y: number } {
  const maxLine = Math.ceil(max / 10);
  const line = Math.floor(order / 10);
  const lineDelta = -Math.max(0, Math.ceil(maxLine - 5));
  return {
    x: 10.25 + (order % 10) * 0.6,
    y: maxLine - line + lineDelta,
  };
}

function initCameraForMarbleCount(camera: Camera, totalCount: number, canvasW: number, canvasH: number) {
  if (totalCount <= 0) return;
  const cols = Math.min(totalCount, 10);
  const rows = Math.ceil(totalCount / 10);
  const lineDelta = -Math.max(0, Math.ceil(rows - 5));
  const centerX = 10.25 + (cols - 1) * 0.3;
  const centerY = (1 + rows) / 2 + lineDelta;
  const spawnWidth = Math.max((cols - 1) * 0.6, 1);
  const spawnHeight = Math.max(rows - 1, 1);
  const margin = 3;
  const viewW = canvasW / initialZoom;
  const viewH = canvasH / initialZoom;
  const zoom = Math.max(
    1.5,
    Math.min(Math.min(viewW / (spawnWidth + margin * 2), viewH / (spawnHeight + margin * 2)), 3)
  );
  camera.initializePosition({ x: centerX, y: centerY }, zoom);
}

function renderEntities(ctx: CanvasRenderingContext2D, entities: MapEntityState[], flashes: Map<string, number>) {
  ctx.save();
  entities.forEach(entity => {
    const transform = ctx.getTransform();
    ctx.translate(entity.x, entity.y);
    ctx.rotate(entity.angle);
    ctx.fillStyle = entity.shape.color ?? themeDark.boxFill;
    ctx.strokeStyle = entity.shape.color ?? themeDark.boxOutline;
    ctx.lineWidth = 0.08;
    ctx.shadowColor = entity.shape.color ?? '#72d9df';
    ctx.shadowBlur = 8;
    ctx.lineCap = 'round';
    const shape = entity.shape;
    switch (shape.type) {
      case 'polyline':
        if (shape.points.length > 0) {
          ctx.strokeStyle = entity.shape.color ?? themeDark.polyline;
          ctx.beginPath();
          ctx.moveTo(shape.points[0][0], shape.points[0][1]);
          for (let i = 1; i < shape.points.length; i++) {
            ctx.lineTo(shape.points[i][0], shape.points[i][1]);
          }
          ctx.stroke();
        }
        break;
      case 'box': {
        const w = shape.width * 2;
        const h = shape.height * 2;
        ctx.rotate(shape.rotation);
        ctx.fillRect(-w / 2, -h / 2, w, h);
        ctx.strokeRect(-w / 2, -h / 2, w, h);
        break;
      }
      case 'circle':
        ctx.strokeStyle = entity.shape.color ?? themeDark.circleOutline;
        ctx.beginPath();
        ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
        ctx.fill(); ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.beginPath(); ctx.arc(0, 0, shape.radius * 0.63, 0, Math.PI * 2);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 0.07; ctx.stroke();
        ctx.fillStyle = '#26203e'; ctx.font = 'bold 0.35px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('✦', 0, 0);
        const hitAt = flashes.get(`${entity.x}:${entity.y}`);
        const age = hitAt == null ? 1000 : performance.now() - hitAt;
        if (age < 450) {
          ctx.beginPath(); ctx.arc(0, 0, shape.radius + age / 450, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,255,255,${1 - age / 450})`; ctx.stroke();
          ctx.fillStyle = '#fff'; ctx.font = 'bold 0.4px sans-serif'; ctx.fillText('POP!', 0, -1.1 - age / 900);
        }
        break;
    }
    ctx.setTransform(transform);
  });
  ctx.restore();
}

const CANVAS_W = 720;
const CANVAS_H = 480;
const UPDATE_MS = 10;

const PinballLottery: React.FC<PinballLotteryProps> = ({ students }) => {
  const [mapIndex, setMapIndex] = useState(0);
  const [engineReady, setEngineReady] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [winners, setWinners] = useState<Student[]>([]);
  const [showRanking, setShowRanking] = useState(false);
  const [bumperHits, setBumperHits] = useState(0);
  const hitCountRef = useRef(0);
  const flashesRef = useRef(new Map<string, number>());
  const contactsRef = useRef(new Set<string>());
  const [engineError, setEngineError] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1);
  const speedRef = useRef(1);
  speedRef.current = speed;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const physicsRef = useRef<Box2dPhysics | null>(null);
  const cameraRef = useRef(new Camera());
  const marblesRef = useRef<SimMarble[]>([]);
  const stageRef = useRef<StageDef | null>(null);

  const rafRef = useRef(0);
  const lastTimeRef = useRef(0);
  const elapsedRef = useRef(0);
  const finishRankRef = useRef(0);
  const isRunningRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
      const p = new Box2dPhysics();
      await p.init();
      if (!cancelled) {
        physicsRef.current = p;
        setEngineReady(true);
      }
      } catch { if (!cancelled) setEngineError("경기장을 불러오지 못했어요. 페이지를 새로고침해 주세요."); }
    })();
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      isRunningRef.current = false;
      if (physicsRef.current) {
        physicsRef.current.clearMarbles();
        physicsRef.current.clear();
        physicsRef.current = null;
      }
    };
  }, []);

  const drawFrame = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const physics = physicsRef.current;
    const stage = stageRef.current;
    if (!ctx || !canvas || !physics || !stage) return;

    const camera = cameraRef.current;
    const entities = physics.getEntities();

    ctx.fillStyle = '#201b35';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.scale(initialZoom, initialZoom);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = '0.4pt sans-serif';
    ctx.lineWidth = 3 / (camera.zoom + initialZoom);

    const activeForCam = marblesRef.current.filter(m => !m.finished).sort((a, b) => b.lastY - a.lastY);
    const targetIndex = 0;
    const leader = activeForCam[targetIndex];
    const goalDist = leader ? Math.abs(stage.zoomY - leader.lastY) : Infinity;
    const needToZoom = goalDist < zoomThreshold;

    camera.update({
      marbles: activeForCam.map(m => ({ position: { x: m.lastX, y: m.lastY, angle: m.lastAngle } })),
      stage,
      needToZoom: isRunningRef.current && activeForCam.length > 0 && needToZoom,
      targetIndex,
    });

    camera.renderScene(ctx, () => {
      ctx.strokeStyle = '#362e50';
      ctx.lineWidth = 0.025;
      for (let y = -5; y < stage.goalY + 8; y += 2) {
        ctx.beginPath(); ctx.moveTo(5, y); ctx.lineTo(21, y); ctx.stroke();
      }
      renderEntities(ctx, entities, flashesRef.current);
      ctx.fillStyle = '#ff6f0f';
      ctx.fillRect(11, stage.goalY, 4, 0.15);
      ctx.font = 'bold 0.5px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('FINISH', 13, stage.goalY + 0.6);

      marblesRef.current.filter(m => !m.finished).forEach(m => {
        const initial = m.student.name?.[0] || '?';
        const hue = Array.from(m.student.uid).reduce((value, char) => (value * 31 + char.charCodeAt(0)) % 360, 0);
        const x = m.lastX;
        const y = m.lastY;
        const angle = m.lastAngle;
        const r = 0.25;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);
        const g = ctx.createRadialGradient(-0.06, -0.06, 0.02, 0, 0, r);
        g.addColorStop(0, '#ffffff');
        g.addColorStop(0.45, `hsl(${hue} 100% ${themeDark.marbleLightness}%)`);
        g.addColorStop(1, `hsl(${hue} 90% 55%)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.25)';
        ctx.lineWidth = 0.02;
        ctx.stroke();
        ctx.rotate(-angle);
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.font = 'bold 0.35px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initial, 0, 0);
        ctx.fillStyle = '#fff'; ctx.font = 'bold 0.3px sans-serif';
        ctx.fillText(m.student.name || '?', 0, 0.5);
        ctx.restore();
      });
    });

    ctx.restore();
  }, []);

  const stopLoop = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    lastTimeRef.current = 0;
    elapsedRef.current = 0;
  };

  const finishRun = () => {
    isRunningRef.current = false;
    setIsRunning(false);
    const ordered = [...marblesRef.current]
      .filter(m => m.finishOrder != null)
      .sort((a, b) => (a.finishOrder ?? 0) - (b.finishOrder ?? 0));
    setWinners(ordered.map(m => m.student));
    setShowRanking(true);
    const stage = stageRef.current;
    if (stage) cameraRef.current.initializePosition({ x: 13, y: stage.goalY - 6 }, 1);
  };

  const startAnimation = () => {
    const physics = physicsRef.current;
    if (!engineReady || !physics || students.length === 0 || isRunningRef.current) return;

    stopLoop();
    setShowRanking(false);
    setWinners([]);
    hitCountRef.current = 0; setBumperHits(0); flashesRef.current.clear(); contactsRef.current.clear();
    finishRankRef.current = 0;

    physics.clearMarbles();
    physics.clear();

    const stage = stages[mapIndex];
    stageRef.current = stage;
    physics.createStage(stage);

    const shuffled = [...students];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const sim: SimMarble[] = shuffled.map((student, order) => {
      const { x, y } = marbleSpawnXY(order, students.length);
      physics.createMarble(order, x, y);
      return {
        id: order,
        student,
        finished: false,
        lastX: x,
        lastY: y,
        lastAngle: 0,
        stuckMs: 0,
      };
    });
    marblesRef.current = sim;

    initCameraForMarbleCount(cameraRef.current, students.length, CANVAS_W, CANVAS_H);
    cameraRef.current.startFollowingMarbles();

    physics.start();
    isRunningRef.current = true;
    setIsRunning(true);

    const tick = (now: number) => {
      const p = physicsRef.current;
      const st = stageRef.current;
      if (!p || !st) return;

      if (!lastTimeRef.current) lastTimeRef.current = now;
      const dt = now - lastTimeRef.current;
      lastTimeRef.current = now;
      elapsedRef.current += Math.min(dt, 100) * speedRef.current;

      const intervalSec = UPDATE_MS / 1000;
      while (elapsedRef.current >= UPDATE_MS) {
        p.step(intervalSec);
        const bumpers = p.getEntities().filter(entity => entity.shape.type === 'circle');
        const nextContacts = new Set<string>();
        for (const m of marblesRef.current) {
          if (m.finished) continue;
          const pos = p.getMarblePosition(m.id);
          for (const bumper of bumpers) {
            if (bumper.shape.type !== 'circle') continue;
            if (Math.hypot(pos.x - bumper.x, pos.y - bumper.y) < bumper.shape.radius + 0.3) {
              const key = `${m.id}:${bumper.x}:${bumper.y}`; nextContacts.add(key);
              if (!contactsRef.current.has(key)) {
                flashesRef.current.set(`${bumper.x}:${bumper.y}`, performance.now());
                hitCountRef.current++; setBumperHits(hitCountRef.current);
              }
            }
          }
          const movement = Math.hypot(pos.x - m.lastX, pos.y - m.lastY);
          m.stuckMs = movement < 0.001 ? m.stuckMs + UPDATE_MS : 0;
          if (m.stuckMs > 4000) { p.shakeMarble(m.id); m.stuckMs = 0; }
          m.lastX = pos.x;
          m.lastY = pos.y;
          m.lastAngle = pos.angle;
          if (pos.y > st.goalY) {
            m.finished = true;
            finishRankRef.current += 1;
            m.finishOrder = finishRankRef.current;
            p.removeMarble(m.id);
            setWinners([...marblesRef.current].filter(ball => ball.finished).sort((a, b) => (a.finishOrder ?? 0) - (b.finishOrder ?? 0)).map(ball => ball.student));
          }
        }
        contactsRef.current = nextContacts;
        elapsedRef.current -= UPDATE_MS;
      }

      drawFrame();

      const allDone = marblesRef.current.every(m => m.finished);
      if (allDone && isRunningRef.current) {
        finishRun();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  };

  /** 트랙 변경 시 이전 루프를 정리하고 미리보기를 그립니다. */
  useEffect(() => {
    const p = physicsRef.current;
    if (!p || !engineReady) return;
    stopLoop();
    p.clearMarbles();
    p.clear();
    const st = stages[mapIndex];
    stageRef.current = st;
    p.createStage(st);
    marblesRef.current = [];
    cameraRef.current.initializePosition({ x: 13, y: st.goalY / 2 }, 0.6);
    drawFrame();
  }, [mapIndex, engineReady, drawFrame]);

  const resetRace = () => {
    stopLoop(); isRunningRef.current = false; setIsRunning(false);
    setWinners([]); setShowRanking(false); marblesRef.current = [];
    setBumperHits(0); hitCountRef.current = 0; flashesRef.current.clear(); contactsRef.current.clear();
    const p = physicsRef.current;
    if (p) { p.clearMarbles(); p.clear(); p.createStage(stages[mapIndex]); }
    cameraRef.current.initializePosition({ x: 13, y: stages[mapIndex].goalY / 2 }, 0.6); drawFrame();
  };

  return (
    <Card style={{ padding: '0', overflow: 'hidden' }}>
      <div className="maru-race">
        <header className="maru-race-header">
          <div><span className="maru-race-eyebrow">MARU PLAY · 01</span>
            <h2>핀볼 레이스<span className="maru-race-dot">●</span></h2>
            <p>팡! 팡! 범퍼를 지나 마지막까지 짜릿한 역전 레이스.</p>
          </div>
          <span className="maru-race-status">{isRunning ? '● 레이스 진행 중' : showRanking ? '✓ 레이스 완료' : '○ 출발 준비'}</span>
        </header>
        <div className="maru-race-layout">
          <section className="maru-race-arena" aria-label="구슬 경기장">
            <div className="maru-race-arena-top"><strong>{stages[mapIndex].title}</strong><span>POP {String(bumperHits).padStart(3, '0')} · {winners.length}/{students.length} FINISH</span></div>
            <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} role="img" aria-label="학생 이름이 표시된 구슬이 결승선으로 내려가는 레이스" />
            <div className="maru-race-arena-bottom"><span>범퍼에 튕겨도 괜찮아. 결승선까지 달려!</span><span>MARU</span></div>
          </section>
          <aside className="maru-race-sidebar">
            <h3>오늘의 경기</h3>
            <label className="maru-race-field">트랙 선택<select value={mapIndex} disabled={isRunning} onChange={e => { setMapIndex(Number(e.target.value)); setWinners([]); setShowRanking(false); }}>{stages.map((s, i) => <option key={s.title} value={i}>{s.title}</option>)}</select></label>
            <label className="maru-race-field">진행 속도 <span>{speed}배</span><input type="range" min="0.5" max="2" step="0.5" value={speed} onChange={e => setSpeed(Number(e.target.value))} /></label>
            <div className="maru-race-participants"><h3>참가자 <span>{students.length}</span></h3><div>{students.map(student => <span key={student.uid} className={winners.some(w => w.uid === student.uid) ? 'arrived' : ''}>{student.name}</span>)}</div></div>
            {engineError && <p role="alert">{engineError}</p>}
            {students.length === 0 && <p>참가할 학생을 먼저 선택해 주세요.</p>}
            <button className="maru-race-start" onClick={startAnimation} disabled={!engineReady || isRunning || students.length === 0}>{engineError ? '경기장 연결 실패' : !engineReady ? '경기장 준비 중…' : isRunning ? '구슬들이 달리고 있어요…' : showRanking ? '한 번 더 달리기 ↗' : '레이스 시작 ↗'}</button>
            <button className="maru-race-reset" onClick={resetRace} disabled={!engineReady}> {isRunning ? '경기 중단 · 초기화' : '처음으로'}</button>
            <p className="maru-race-note">패들은 자동으로 회전해요. 출발 자리는 매번 무작위!</p>
          </aside>
        </div>
        <section className="maru-race-results" aria-live="polite">
          <h3>{showRanking ? '우리 반 레이스 결과' : '결승선 소식'} <span>{winners.length > 0 ? `${winners.length}명 도착` : '두근두근, 첫 도착을 기다려요'}</span></h3>
          {winners.length === 0 ? <p>레이스를 시작하면 도착한 순서대로 여기에 표시됩니다.</p> : <div className="maru-race-ranking">{winners.map((student, index) => <div key={student.uid} className={index === 0 ? 'first' : ''}><b>{index === 0 ? '🏆' : index + 1}</b><strong>{student.name}</strong><span>{index === 0 ? '첫 번째 행운' : `${index + 1}등`}</span></div>)}</div>}
        </section>
      </div>
    </Card>
  );
};

export default PinballLottery;
